import { useMemo } from 'react';
import { useContent } from '../contexts/ContentContext';
import { HomeBodySlideList } from '../content/keys';

/**
 * useHomeSlides
 *
 * Parses the translated HomeBodySlideList CMS value (same legacy format used by
 * `tlffs-frontend` — a semicolon-separated list of JSON objects per slide) into
 * a normalized array of slide descriptors.
 *
 * Legacy slide shape:
 *   { Url: string, Title?: string, SubTitle?: string, LinkUrl?: string, LinkText?: string }
 *
 * Falls back to `mainDraw.gallery_images` (a flat `string[]`) when the content
 * key is empty/missing, preserving current v3 behavior for orgs that haven't
 * populated HomeBodySlideList yet.
 *
 * Re-computes automatically on language change — `useContent().c` returns a new
 * value when ContentContext refetches the active language, so the useMemo
 * dependency on the raw string handles this without a manual i18n subscription.
 *
 * @param {{ gallery_images?: string[] }} mainDraw
 * @param {{ gameType?: string }} [options]
 * @returns {Array<{ url: string, title?: string, subtitle?: string, linkUrl?: string, linkText?: string }>}
 */
export default function useHomeSlides(mainDraw, options = {}) {
  const { c } = useContent();
  const { gameType } = options;

  // Prefer a game-type-scoped key if the CMS has one, else the shared key.
  // (Kept as a forward hook point — today only the shared key exists in content/keys.)
  const scopedKey = gameType ? `${HomeBodySlideList}_${gameType}` : null;
  const scopedRaw = scopedKey ? c(scopedKey, '') : '';
  const sharedRaw = c(HomeBodySlideList, '');
  const raw = scopedRaw || sharedRaw;

  const galleryImages = mainDraw?.gallery_images || [];

  return useMemo(() => {
    const parsed = parseSlideList(raw);
    if (parsed.length > 0) return parsed;

    // Fallback: flat gallery_images as image-only slides.
    return galleryImages
      .filter((u) => typeof u === 'string' && u.trim().length > 0)
      .map((url) => ({ url }));
    // raw and galleryImages.join both captured below to avoid reference-equality churn on array re-creation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, galleryImages.join('|')]);
}

function parseSlideList(raw) {
  if (!raw || typeof raw !== 'string') return [];
  return raw
    .split(';')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => {
      try {
        const obj = JSON.parse(chunk);
        return normalizeSlide(obj);
      } catch (e) {
        // Match legacy behavior — warn and skip malformed slides.
        // eslint-disable-next-line no-console
        console.warn('[useHomeSlides] Failed to parse slide:', chunk);
        return null;
      }
    })
    .filter((s) => s && s.url);
}

function normalizeSlide(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const url = obj.Url || obj.url;
  if (!url || typeof url !== 'string') return null;

  const title = firstString(obj.Title, obj.title);
  const subtitle = firstString(obj.SubTitle, obj.Subtitle, obj.subtitle);
  const linkUrl = firstString(obj.LinkUrl, obj.linkUrl);
  const linkText = firstString(obj.LinkText, obj.linkText);

  const slide = { url };
  if (title) slide.title = title;
  if (subtitle) slide.subtitle = subtitle;
  if (linkUrl) slide.linkUrl = linkUrl;
  if (linkText) slide.linkText = linkText;
  return slide;
}

function firstString(...values) {
  for (const v of values) {
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return undefined;
}
